import { useMemo } from 'react'

import { ChartData, ChartOptions } from 'chart.js'
import { Chart } from 'react-chartjs-2'

import { NETWORK_GROWTH_HISTOGRAM_HEIGHT } from 'components/reports/Metrics/constants'

import { BAR_STYLE } from '../chartConfig'
import { NetworkGrowthBucket } from '../types'

interface Props {
  buckets: NetworkGrowthBucket[]
  color: string
  height?: number
}

export const JobsPerSupplierChart = ({
  buckets,
  color,
  height = NETWORK_GROWTH_HISTOGRAM_HEIGHT,
}: Props) => {
  const { labels, percentages, counts } = useMemo(
    () => ({
      labels: buckets.map((bucket) => bucket.label),
      percentages: buckets.map((bucket) => Number(bucket.percentage)),
      counts: buckets.map((bucket) => bucket.supplierCount),
    }),
    [buckets],
  )

  const data = useMemo<ChartData<'bar', number[], string>>(
    () => ({
      labels,
      datasets: [
        {
          ...BAR_STYLE,
          type: 'bar' as const,
          label: '% of suppliers',
          data: percentages,
          backgroundColor: color,
        },
      ],
    }),
    [labels, percentages, color],
  )

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: { display: false },
          title: { display: true, text: 'Number of jobs completed' },
          ticks: { maxRotation: 0, autoSkip: false },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: '% of suppliers' },
          ticks: { callback: (value) => `${value}%` },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${percentages[context.dataIndex]}% — ${counts[
                context.dataIndex
              ].toLocaleString()} supplier(s)`,
          },
        },
      },
    }),
    [percentages, counts],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'bar', number[], string> type='bar' data={data} options={options} />
    </div>
  )
}
